import React, { useEffect, useMemo, useState, type ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ConfigProvider } from 'antd'

import App from './App'
import { persistor, store } from './utils/redux/store'
import { setupInterceptors } from './utils/api'
import { useAppSelector } from './utils/redux/store'
import { THEME_ID } from './utils/redux/appSlice'
import { getAntdTheme, resolveThemeMode } from './utils/theme/antdThemeConfig'

import './index.scss'

// ✅ передаём store
setupInterceptors(store)

function ThemeRuntime({ children }: { children: ReactNode }) {
  const themeId = useAppSelector(
    (s: any) => (s.app?.themeId as THEME_ID) ?? THEME_ID.DARK,
  )

  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }

    // Safari fallback
    // @ts-ignore
    mql.addListener(onChange)
    // @ts-ignore
    return () => mql.removeListener(onChange)
  }, [])

  const mode = useMemo(() => resolveThemeMode(themeId, systemDark), [themeId, systemDark])

  useEffect(() => {
    const root = document.documentElement
    root.dataset.theme = mode
    root.style.colorScheme = mode
  }, [mode])

  const antdTheme = useMemo(
    () => getAntdTheme(themeId, systemDark),
    [themeId, systemDark],
  )

  return <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeRuntime>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeRuntime>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
)
